/**
 * 企业端 — 运输单详情（状态机流转 / 围栏配置 / 证照上传 / 档案）
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  arriveOrder,
  cancelOrder,
  configureFences,
  departOrder,
  fetchArchive,
  fetchFences,
  fetchOrder,
  fetchQuarantine,
  fetchTrack,
  signOrder,
  triggerArchive,
  uploadCert,
  verifyCert,
} from '@/api/transit';
import TrackMap from '@/components/common/TrackMap';
import { Empty, Loading, OrderStatusTag, StatusTag } from '@/components/common';
import type { GPSPoint, QuarantineCert, TransitArchive, TransitFence, TransitOrder } from '@/types';
import { certTypeText, certVerifyText, fenceTypeText, fmtTime, quarantineStatusText } from '@/utils/format';

export default function EntOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const transitId = Number(id ?? 0);

  const [order, setOrder] = useState<TransitOrder | null>(null);
  const [points, setPoints] = useState<GPSPoint[]>([]);
  const [fences, setFences] = useState<TransitFence[]>([]);
  const [certs, setCerts] = useState<QuarantineCert[]>([]);
  const [archive, setArchive] = useState<TransitArchive | null>(null);
  const [loading, setLoading] = useState(true);

  const [certForm, setCertForm] = useState({ cert_type: 1, cert_no: '', cert_url: '', cattle_count: 0 });
  const [fenceForm, setFenceForm] = useState({ origin_lng: 117.02, origin_lat: 36.65, dest_lng: 118.05, dest_lat: 36.8, radius: 5000 });

  const reload = () => {
    Promise.all([
      fetchOrder(transitId),
      fetchTrack(transitId),
      fetchFences(transitId),
      fetchQuarantine(transitId),
      fetchArchive(transitId),
    ]).then(([o, t, f, c, a]) => {
      setOrder(o);
      setPoints(t);
      setFences(f);
      setCerts(c);
      setArchive(a);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (!transitId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitId]);

  if (loading) return <Loading />;
  if (!order) return <Empty text="运输单不存在" />;

  const act = async (fn: () => Promise<void>, msg: string) => {
    await fn();
    alert(msg);
    reload();
  };

  const canDepart = order.status === 0 && order.quarantine_status === 1;
  const canArrive = order.status === 1;
  const canSign = order.status === 2;
  const canCancel = order.status === 0;

  const submitCert = async () => {
    if (!certForm.cert_no || !certForm.cert_url) {
      alert('请填写证号与证照 URL');
      return;
    }
    const cert = await uploadCert(transitId, certForm);
    await verifyCert(transitId, cert.id);
    alert('证照已上传并提交核验');
    reload();
  };

  const submitFences = async () => {
    await configureFences(transitId, [
      { fence_type: 1, fence_name: '起运点围栏', center_lng: fenceForm.origin_lng, center_lat: fenceForm.origin_lat, radius_m: fenceForm.radius },
      { fence_type: 3, fence_name: '目的地围栏', center_lng: fenceForm.dest_lng, center_lat: fenceForm.dest_lat, radius_m: fenceForm.radius },
    ]);
    alert('围栏配置成功（起运/目的地半径 ' + fenceForm.radius + ' 米）');
    reload();
  };

  const submitArchive = async () => {
    await triggerArchive(transitId);
    alert('已触发档案构建与上链');
    reload();
  };

  return (
    <div>
      <Link to="/enterprise/orders" style={{ fontSize: 13 }}>
        ← 返回运输单列表
      </Link>
      {order.source_order_no ? (
        <div
          className="card"
          style={{ marginTop: 8, padding: '10px 14px', background: 'var(--color-bg-soft, #f6f8fa)' }}
        >
          <span style={{ fontSize: 13 }}>
            🔗 来源：交易订单 {order.source_order_no}
            <a
              href={`https://trade.yunniuji.com/orders/${order.order_id}`}
              target="_blank"
              rel="noreferrer"
              style={{ marginLeft: 8 }}
            >
              查看交易订单详情 ↗
            </a>
          </span>
        </div>
      ) : null}
      <div className="row-between" style={{ marginTop: 8, marginBottom: 16 }}>
        <div>
          <div className="page-title">
            {order.transit_no}
            <span style={{ fontSize: 14, marginLeft: 12, color: 'var(--color-ink-secondary)' }}>{order.origin_city} → {order.destination_city}</span>
          </div>
          <div className="page-sub" style={{ marginBottom: 0 }}>
            牛只 {order.cattle_count} 头 · 检疫 {quarantineStatusText(order.quarantine_status)}
          </div>
        </div>
        <OrderStatusTag status={order.status} />
      </div>

      {/* 状态机流转 */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">🚦 状态流转</div>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            disabled={!canDepart}
            title={order.quarantine_status !== 1 ? '检疫核验通过后才可起运' : ''}
            onClick={() => act(() => departOrder(transitId), '已确认起运')}
          >
            确认起运
          </button>
          <button className="btn btn-amber" disabled={!canArrive} onClick={() => act(() => arriveOrder(transitId), '已确认到达')}>
            确认到达
          </button>
          <button
            className="btn btn-green"
            disabled={!canSign}
            onClick={() => act(() => signOrder(transitId, { remark: '牛只健康，签收完成' }), '已签收，档案自动构建')}
          >
            到达签收
          </button>
          <button className="btn btn-red" disabled={!canCancel} onClick={() => act(() => cancelOrder(transitId), '运输单已取消')}>
            取消运输单
          </button>
        </div>
        {order.quarantine_status !== 1 && order.status === 0 && (
          <p style={{ fontSize: 12, color: 'var(--color-red)', marginTop: 10 }}>⚠ 检疫未通过/未核验，禁止起运（错误码 3002）</p>
        )}
      </div>

      <div className="grid grid-2">
        {/* 围栏配置 */}
        <div className="card">
          <div className="card-title">⭕ 电子围栏配置</div>
          <div className="grid grid-2">
            <div className="field">
              <label>起运点经度</label>
              <input type="number" step="0.000001" value={fenceForm.origin_lng} onChange={(e) => setFenceForm((f) => ({ ...f, origin_lng: Number(e.target.value) }))} />
            </div>
            <div className="field">
              <label>起运点纬度</label>
              <input type="number" step="0.000001" value={fenceForm.origin_lat} onChange={(e) => setFenceForm((f) => ({ ...f, origin_lat: Number(e.target.value) }))} />
            </div>
            <div className="field">
              <label>目的地经度</label>
              <input type="number" step="0.000001" value={fenceForm.dest_lng} onChange={(e) => setFenceForm((f) => ({ ...f, dest_lng: Number(e.target.value) }))} />
            </div>
            <div className="field">
              <label>目的地纬度</label>
              <input type="number" step="0.000001" value={fenceForm.dest_lat} onChange={(e) => setFenceForm((f) => ({ ...f, dest_lat: Number(e.target.value) }))} />
            </div>
          </div>
          <div className="field">
            <label>半径（米，默认 5000）</label>
            <input type="number" min={100} value={fenceForm.radius} onChange={(e) => setFenceForm((f) => ({ ...f, radius: Number(e.target.value) }))} />
          </div>
          <button className="btn btn-primary" onClick={submitFences}>
            保存围栏
          </button>
          {fences.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 13 }}>
              {fences.map((f) => (
                <div key={f.id} className="row-between" style={{ padding: '6px 0', borderBottom: '1px solid var(--color-line)' }}>
                  <span>
                    {fenceTypeText(f.fence_type)} · {f.fence_name || '未命名'}
                  </span>
                  <span style={{ color: 'var(--color-ink-secondary)' }}>半径 {f.radius_m}m</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 检疫证照 */}
        <div className="card">
          <div className="card-title">📋 检疫证照（A/B 证）</div>
          <div className="grid grid-2">
            <div className="field">
              <label>证照类型</label>
              <select value={certForm.cert_type} onChange={(e) => setCertForm((f) => ({ ...f, cert_type: Number(e.target.value) }))}>
                <option value={1}>A证（跨省）</option>
                <option value={2}>B证（省内）</option>
              </select>
            </div>
            <div className="field">
              <label>关联牛只</label>
              <input type="number" min={0} value={certForm.cattle_count} onChange={(e) => setCertForm((f) => ({ ...f, cattle_count: Number(e.target.value) }))} />
            </div>
          </div>
          <div className="field">
            <label>证号（A/B + 至少 6 位数字）</label>
            <input value={certForm.cert_no} onChange={(e) => setCertForm((f) => ({ ...f, cert_no: e.target.value }))} placeholder="如 A370100202608010001" />
          </div>
          <div className="field">
            <label>证照电子件 URL</label>
            <input value={certForm.cert_url} onChange={(e) => setCertForm((f) => ({ ...f, cert_url: e.target.value }))} placeholder="https://oss.example.com/cert/xxx.jpg" />
          </div>
          <button className="btn btn-primary" onClick={submitCert}>
            上传并核验
          </button>
          {certs.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 13 }}>
              {certs.map((c) => (
                <div key={c.id} className="row-between" style={{ padding: '6px 0', borderBottom: '1px solid var(--color-line)' }}>
                  <span>
                    {certTypeText(c.cert_type)} · <span style={{ fontFamily: 'monospace' }}>{c.cert_no}</span>
                  </span>
                  <StatusTag tone={c.verify_status === 1 ? 'green' : c.verify_status === 2 ? 'red' : c.verify_status === 3 ? 'amber' : 'muted'}>
                    {certVerifyText(c.verify_status)}
                  </StatusTag>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 轨迹 + 档案 */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title">🗺 轨迹（{points.length} 点）</div>
        <TrackMap points={points} fences={fences.map((f) => ({ lng: f.center_lng, lat: f.center_lat, radius_m: f.radius_m, fence_type: f.fence_type, fence_name: f.fence_name }))} />
      </div>

      <div className="card">
        <div className="card-title">
          <span>🔗 运输档案</span>
          <button className="btn btn-sm btn-primary" onClick={submitArchive}>
            触发上链
          </button>
        </div>
        {archive ? (
          <div style={{ fontSize: 13, lineHeight: 2, fontFamily: 'monospace' }}>
            <div>档案 ID：{archive.id} · 状态：
              <StatusTag tone={archive.status === 1 ? 'green' : archive.status === 2 ? 'red' : 'amber'}>
                {archive.status === 1 ? '已上链' : archive.status === 2 ? '失败' : '待上链'}
              </StatusTag>
            </div>
            <div>轨迹哈希：{archive.track_hash}</div>
            <div>证照哈希：{archive.cert_hash}</div>
            <div>签收哈希：{archive.sign_hash}</div>
            <div>交易哈希：{archive.tx_hash || '—'} · 上链时间：{fmtTime(archive.block_time)}</div>
          </div>
        ) : (
          <Empty text="签收后将自动构建档案，也可手动触发上链" />
        )}
      </div>
    </div>
  );
}
